import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

import { CsvSeparator } from '../file-export/file-export.service';

/**
 * Guess a CSV's separator from its header row: whichever of `;` / `,` it has
 * more of, ignoring quoted text. Only the header is looked at — data rows carry
 * French decimal commas (`8,5`) that would skew a `;` file towards `,`.
 * Null when the header has neither, or as many of each.
 */
export function detectCsvSeparator(text: string): CsvSeparator | null {
  const header = text.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] ?? '';
  let semicolons = 0;
  let commas = 0;
  let quoted = false;
  for (const char of header) {
    if (char === '"') quoted = !quoted;
    else if (!quoted && char === ';') semicolons++;
    else if (!quoted && char === ',') commas++;
  }
  if (semicolons === commas) return null;
  return semicolons > commas ? ';' : ',';
}

@Injectable({ providedIn: 'root' })
export class FileImportService {
  /**
   * The separator a CSV most likely uses, to pre-select in `CsvSeparatorDialog`
   * — a suggestion only, the user still confirms it. Null when unsure.
   */
  async guessCsvSeparator(file: File): Promise<CsvSeparator | null> {
    const buffer = await file.arrayBuffer();
    // Both separators are ASCII, so a lenient UTF-8 decode is fine for any
    // encoding the import accepts.
    const head = new TextDecoder('utf-8').decode(buffer.slice(0, 4096));
    return detectCsvSeparator(head);
  }

  async readSheet(
    file: File,
    separator: CsvSeparator = ',',
    opts: { cellDates?: boolean; sheetIndex?: number; raw?: boolean } = {},
  ): Promise<string[][]> {
    const cellDates = opts.cellDates ?? false;
    const sheetIndex = opts.sheetIndex ?? 0;
    // `raw: true` keeps each cell's literal text instead of letting SheetJS infer
    // numbers/dates — needed for code-like strings (e.g. waste codes "02 03 04")
    // that would otherwise be coerced into a date or numeric serial.
    const raw = opts.raw ?? false;
    const buffer = await file.arrayBuffer();
    const text = this.csvUtf8Text(file, buffer);
    const wb = XLSX.read(text ?? buffer, {
      type: text === null ? 'array' : 'string',
      FS: separator,
      cellDates,
      raw,
    });
    return XLSX.utils.sheet_to_json<string[]>(wb.Sheets[wb.SheetNames[sheetIndex]], {
      header: 1,
      defval: '',
      raw,
    });
  }

  /**
   * A UTF-8 CSV as text: SheetJS reads one without a byte-order mark as
   * Latin-1 ("Unité" -> "UnitÃ©"). Null for xlsx and non-UTF-8 CSVs
   * (Windows-1252, UTF-16), which SheetJS already reads right from the bytes.
   */
  private csvUtf8Text(file: File, buffer: ArrayBuffer): string | null {
    if (!file.name.toLowerCase().endsWith('.csv')) return null;
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
      return null;
    }
  }

  async getSheetNames(file: File): Promise<string[]> {
    const buffer: ArrayBuffer = await file.arrayBuffer();
    const workbook: XLSX.WorkBook = XLSX.read(buffer, { type: 'array' });
    return workbook.SheetNames;
  }
}
